import { css, Global } from '@emotion/react';
import styled from '@emotion/styled';
import {
    FontAwesomeIcon,
    FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import {
    faMapLocation,
    faFilterList,
    faList,
} from '@fortawesome/pro-regular-svg-icons';
import {
    ButtonGroup,
    ButtonGroupButton,
    mqDown,
    mqUp,
    ThemeBreakpoint,
    useFilter,
} from 'rwl-ui';
import { isShowingJobList, toggleMap, toggleSidebar } from './utils';
import { Content } from './Content';
import { useState } from 'react';
import { Header } from './Header/Header';
import { CTABar } from '../components/CTABar';
import { SidebarCloseButton } from './SidebarCloseButton';
import { When } from 'react-if';
import { useGlobal } from '../hooks';

export type SharedLayoutProps = {
    Sidebar?: JSX.Element | null;
    MapContent?: JSX.Element | null;
    JobListContent?: JSX.Element | null;
} & Record<string, any>;

const GLOBAL_CSS = css`
    :root {
        --rwl-sidebar-width: 240px;
        --rwl-header-height: 80px;
        --rwl-xs-header-height: 155px;
        --rwl-minimum-screen-size: 320px;
        --rwl-filter-bar-zIndex: 10;
        --rwl-job-story-zIndex: 20;
        --rwl-media-modal-zIndex: 30;
        box-sizing: border-box;
    }

    *,
    *:before,
    *:after {
        box-sizing: inherit;
    }

    *::-webkit-scrollbar {
        width: 12px; /* width of the entire scrollbar */
    }

    *::-webkit-scrollbar-track {
        background-color: transparent; /* color of the tracking area */
    }

    *::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.2); /* color of the scroll thumb */
        border-radius: 6px; /* roundness of the scroll thumb */
        width: 8px;
    }

    body {
        position: relative;
        margin: 0;
        -webkit-overflow-scrolling: auto;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;

        width: 100%;
        min-width: var(--rwl-minimum-screen-size);
        height: 100%; /* ios Safari measures 100vh to entire screen even if safari nav takes up some */
    }
`;

const MainContent = styled.div`
    flex: 1;
    display: flex;
    flex-direction: row;
    overflow: hidden;
    position: relative;
`;

const ScreenContainer = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    justify-content: stretch;
    overflow: hidden;
    container-type: inline-size;
    container-name: container;
`;

const SidebarContainer = styled.div`
    min-width: var(--rwl-sidebar-width);
    width: var(--rwl-sidebar-width);
    transition: transform 0.4s, width 0.4s;

    background: #fff;
    overflow-y: scroll;
    padding: var(--rwl-spacing-md) var(--rwl-spacing-lg);

    // Mui elevation 6
    box-shadow: 0 1px 18px 0 rgba(0, 0, 0, 0.12),
        0 6px 10px 0 rgba(0, 0, 0, 0.14), 0 3px 5px -1px rgba(0, 0, 0, 0.2);

    ${mqDown('xs')} {
        width: 100%;
    }

    ${mqDown('md')} {
        transform: translateX(calc(100% * (var(--rwl-sidebar-visible, 0) - 1)));
        position: absolute;
        inset: 0;
        z-index: 10;
    }

    ${mqUp('md')} {
        transform: none;
        position: sticky;
    }

    .FilterNestedMultiListItemTitle {
        margin-left: var(--rwl-spacing-sm);
    }

    .FilterNestedMultiListSubFiltersLast {
        margin-left: var(--rwl-spacing-sm);
    }
`;

const HeaderExtraContent = styled.div(
    ({ breakpoint }: { breakpoint?: ThemeBreakpoint }) => `
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: end;
    width: 100%;

    ${breakpoint && mqDown(breakpoint)} {
        flex-direction: column;
        justify-content: center;
    }
`
);

const SmallScreenContainer = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    bottom: var(--rwl-header-height);
    display: flex;
    flex-direction: row;
    justify-content: center;
    flex: 1;
    opacity: 1;

    transition: opacity 0.2s ease-in-out;

    ${mqUp('md')} {
        opacity: 0;
        pointer-events: none;
        cursor: default;
    }
`;

const FilterButton = styled(ButtonGroupButton)`
    && {
        justify-content: center;
        color: var(--rwl-palette-white);
        height: 2.75em;
    }
`;
const MapButton = styled(ButtonGroupButton)`
    && {
        justify-content: center;
        color: var(--rwl-palette-white);
        height: 2.75em;

        border-left: 1px solid rgba(255, 255, 255, 0.3);

        ${mqUp('sm')} {
            display: none;
        }
    }
`;

const ButtonIcon = (props: FontAwesomeIconProps): JSX.Element => (
    <FontAwesomeIcon style={{ fontSize: '1.375em' }} {...props} />
);

const FilterCount = styled.span`
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    background-color: var(--rwl-palette-white);
    color: var(--rwl-color-primary);
    font-size: 0.75em;
    font-weight: 700;
    text-align: center;
    line-height: 1rem;
    padding-right: 1px; // offset because the font doesn't center perfectly
`;

export const Layout = (props: SharedLayoutProps) => {
    const [, forceRerender] = useState(0);
    const { appliedFilters } = useFilter();
    const { config } = useGlobal();

    //these will be undefined in portfolio (not widget) so we want them to default to true
    const headerEnabled = config?.enableHeader ?? true;
    const filtersEnabled = config?.enableFilters ?? true;
    return (
        <ScreenContainer>
            <Global styles={GLOBAL_CSS} />
            <When condition={headerEnabled}>
                <Header breakpoint={'xs'}>
                    <HeaderExtraContent breakpoint={'xs'}>
                        <CTABar />
                    </HeaderExtraContent>
                </Header>
            </When>
            <MainContent>
                <When condition={filtersEnabled}>
                    <SidebarContainer>
                        {props.Sidebar}
                        <SidebarCloseButton />
                    </SidebarContainer>
                </When>
                <Content {...props} />
                <SmallScreenContainer>
                    <ButtonGroup
                        dark={true}
                        style={{ borderRadius: '1.375em' }}
                    >
                        <When condition={filtersEnabled}>
                            <FilterButton onClick={toggleSidebar}>
                                <ButtonIcon icon={faFilterList} />
                                Filter
                                <When condition={appliedFilters.length}>
                                    <FilterCount>
                                        {appliedFilters.length}
                                    </FilterCount>
                                </When>
                            </FilterButton>
                        </When>
                        <MapButton
                            onClick={() => {
                                toggleMap();
                                forceRerender(Date.now());
                            }}
                        >
                            {!isShowingJobList() ? (
                                <>
                                    <ButtonIcon icon={faList} />
                                    Job list
                                </>
                            ) : (
                                <>
                                    <ButtonIcon icon={faMapLocation} />
                                    <span style={{ whiteSpace: 'nowrap' }}>
                                        Map view
                                    </span>
                                </>
                            )}
                        </MapButton>
                    </ButtonGroup>
                </SmallScreenContainer>
            </MainContent>
        </ScreenContainer>
    );
};
